import type { DpColumnId, DpColumnProfile } from './vacationDpTable.definitions';
import {
    DP_COLUMN_IDS,
    LOCKED_COLUMNS,
    COLUMN_VISIBILITY_BY_PROFILE,
    getColumnVisibilityStorageKey,
} from './vacationDpTable.definitions';

export interface ColumnPreferenceState {
    order: DpColumnId[];
    visibility: Record<DpColumnId, boolean>;
}

export type StoredColumnPreferences = Partial<Record<DpColumnProfile, ColumnPreferenceState>>;

export function getDefaultPreferences(profile: DpColumnProfile): ColumnPreferenceState {
    return {
        order: [...DP_COLUMN_IDS],
        visibility: COLUMN_VISIBILITY_BY_PROFILE[profile] as Record<DpColumnId, boolean>,
    };
}

export function mergeStoredPreferences(
    stored: StoredColumnPreferences | null,
    profile: DpColumnProfile
): ColumnPreferenceState {
    const profilePrefs = stored?.[profile];

    if (!profilePrefs) {
        return getDefaultPreferences(profile);
    }

    const defaults = getDefaultPreferences(profile);

    let order = profilePrefs.order.filter((id) => DP_COLUMN_IDS.includes(id));

    for (const id of DP_COLUMN_IDS) {
        if (!order.includes(id)) {
            order.push(id);
        }
    }

    const visibility: Record<DpColumnId, boolean> = { ...defaults.visibility };
    for (const id of DP_COLUMN_IDS) {
        if (id in profilePrefs.visibility) {
            visibility[id] = profilePrefs.visibility[id];
        }
    }

    for (const locked of LOCKED_COLUMNS) {
        visibility[locked] = true;
    }

    order = order.filter((id) => !LOCKED_COLUMNS.includes(id) || visibility[id]);
    const lockedOrder = LOCKED_COLUMNS.filter((id) => visibility[id]);
    order = [...lockedOrder, ...order.filter((id) => !LOCKED_COLUMNS.includes(id))];

    return { order, visibility };
}

export function arrayMove<T>(array: T[], from: number, to: number): T[] {
    const newArray = [...array];
    const item = newArray.splice(from, 1)[0];
    newArray.splice(to, 0, item);
    return newArray;
}

export function moveColumnInOrder(
    order: DpColumnId[],
    activeId: DpColumnId,
    overId: DpColumnId
): DpColumnId[] {
    const activeIndex = order.indexOf(activeId);
    const overIndex = order.indexOf(overId);

    if (activeIndex === -1 || overIndex === -1) {
        return order;
    }

    if (LOCKED_COLUMNS.includes(activeId) || LOCKED_COLUMNS.includes(overId)) {
        return order;
    }

    return arrayMove(order, activeIndex, overIndex);
}

export function readFromLocalStorage(email: string): StoredColumnPreferences | null {
    try {
        const key = getColumnVisibilityStorageKey(email);
        const stored = localStorage.getItem(key);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (typeof parsed === 'object' && parsed !== null) {
            return parsed;
        }
        return null;
    } catch {
        return null;
    }
}

export function writeToLocalStorage(email: string, prefs: StoredColumnPreferences): void {
    try {
        const key = getColumnVisibilityStorageKey(email);
        localStorage.setItem(key, JSON.stringify(prefs));
    } catch (error) {
        console.warn('Failed to save column preferences to localStorage:', error);
    }
}

export function isCustomized(
    order: DpColumnId[],
    visibility: Record<DpColumnId, boolean>,
    profile: DpColumnProfile
): boolean {
    const defaults = getDefaultPreferences(profile);

    const orderMatch = order.length === defaults.order.length && order.every((id, i) => id === defaults.order[i]);

    if (!orderMatch) return true;

    for (const id of DP_COLUMN_IDS) {
        if (visibility[id] !== defaults.visibility[id]) {
            return true;
        }
    }

    return false;
}

export function applyPreferenceUpdate(
    stored: StoredColumnPreferences | null,
    profile: DpColumnProfile,
    updater: (prefs: ColumnPreferenceState) => ColumnPreferenceState
): StoredColumnPreferences {
    const merged = mergeStoredPreferences(stored, profile);
    const updated = updater(merged);
    const result: StoredColumnPreferences = { ...stored };
    result[profile] = updated;
    return result;
}
